/**
 * Combat.
 *
 * None of this exists in v1 — grep `src/client` for health, damage, hitbox,
 * knockback or projectile and you get zero hits, and remote players are never
 * inserted into collision at all. Player-vs-player contact is new construction.
 *
 * Every number that decides an outcome is read from `@chaos/protocol`'s
 * CHARACTERS table on the server. The client's copy exists to size a telegraph
 * decal and draw a cooldown ring; it is never asked what an ability did.
 *
 * Shape of a cast:
 *
 *   press -> cooldown + resource checked -> committed (`busy`, aim frozen)
 *         -> wind-up elapses -> shape resolved against live targets -> recovery
 *
 * AIM is captured at commit and never re-read, for every ability: a spun
 * camera must not swing an in-flight cast around. ORIGIN depends on
 * `abilityAnchor` from @chaos/protocol — the same predicate the client's
 * telegraphs and VFX consult, so where the hit resolves and where it is drawn
 * can never drift apart:
 *
 *   world — the effect is a place, fixed at cast (Fault Line, ground zones,
 *           projectiles). Resolves from the FROZEN cast origin: the 600ms on
 *           Fault Line is dodgeable precisely because Monolith cannot drag the
 *           crack off the drawn line by sprinting through his own wind-up.
 *   body  — the effect emanates from the caster (melee cones, spheres, self,
 *           Blink Strike). Resolves from the LIVE body at resolve tick: a
 *           90ms swing frozen at the cast position detaches and hangs in the
 *           air behind a walking caster.
 */
import { abilityAnchor, getCharacter, } from "@chaos/protocol";
import { BARRAGE_SHELL_SPLASH, DASH_ABILITY_ID, DASH_COOLDOWN_MS, DASH_DURATION_MS, DECOY_ID_PREFIX, DT, GROUND_NONE, GROUND_ZONE_OFFSET, KILL_CREDIT_TICKS, KILL_HEAL, KILL_ULT_CHARGE, KNOCKBACK_LIFT_FRACTION, KNOCKBACK_SPEED_PER_UNIT, MELEE_VERTICAL_REACH, PATH_HISTORY_TICKS, PLAYER_HALF_HEIGHT, PLAYER_RADIUS, PROJECTILE_CONTACT_RADIUS, PROJECTILE_LAUNCH_COS, PROJECTILE_LAUNCH_SIN, PROJECTILE_SPAWN_OFFSET, PULL_DURATION_MS, TRAP_ARM_TICKS, ULT_CHARGE_PER_DAMAGE_DEALT, ULT_CHARGE_PER_DAMAGE_TAKEN, ULT_FULL, ZONE_HIT_EVENT_INTERVAL_TICKS, } from "./constants.js";
import { applyEffect, damageReductionPct, hasEffect } from "./effects.js";
import { clamp, dcos, dsin, msToTicks, PI, ticksToMs } from "./math.js";
import { nextPointInDisc } from "./rng.js";
import { sweepPoint } from "./collision.js";
import { dist2, dist2XZ, forwardFromYaw, pointRectDist2XZ, rightFromYaw } from "./vec.js";
import { ZONE_SOURCE_ID } from "./zone.js";
// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------
export function findPlayer(world, id) {
    for (const p of world.players)
        if (p.id === id)
            return p;
    return null;
}
export function getAux(world, id) {
    const existing = world.aux[id];
    if (existing)
        return existing;
    const created = createAux();
    world.aux[id] = created;
    return created;
}
export function createAux() {
    return {
        coyoteTicks: 0,
        jumpBufferTicks: 0,
        jumpHeld: false,
        isJumping: false,
        castBufferSlot: null,
        castBufferTicks: 0,
        prevHealth: 0,
        lastHitById: null,
        lastHitAbilityId: "",
        lastHitTick: -1,
        lastZoneTick: -1,
        pathX: new Array(PATH_HISTORY_TICKS).fill(0),
        pathY: new Array(PATH_HISTORY_TICKS).fill(0),
        pathZ: new Array(PATH_HISTORY_TICKS).fill(0),
        pathYaw: new Array(PATH_HISTORY_TICKS).fill(0),
        pathHead: 0,
        standingIndex: GROUND_NONE,
        wasDashing: false,
        dashBufferTicks: 0,
    };
}
export function abilityForSlot(characterId, slot) {
    return getCharacter(characterId).abilities[slot];
}
export function maxHealth(p) {
    return getCharacter(p.characterId).maxHealth;
}
// ---------------------------------------------------------------------------
// Damage
// ---------------------------------------------------------------------------
/**
 * The one place health goes down.
 *
 * Returns the damage actually applied, which is what chain falloff and the
 * `hit` event both need. Returns 0 for anything the target was immune to, so a
 * caller can tell "hit for nothing" from "did not hit".
 */
export function applyDamage(world, target, raw, attackerId, abilityId, events, report = true) {
    if (target.phase !== "alive")
        return 0;
    // The drop phase is invulnerable, so landing on someone's head is not a
    // strategy and spawn-camping a 5s flyover is not possible.
    if (world.tick <= world.dropEndTick)
        return 0;
    if (hasEffect(target, "iframe"))
        return 0;
    if (raw <= 0)
        return 0;
    const dmg = raw * (1 - damageReductionPct(target) / 100);
    if (dmg <= 0)
        return 0;
    target.health -= dmg;
    const aux = getAux(world, target.id);
    // The zone is tracked separately rather than overwriting the last attacker.
    // Otherwise chasing someone to 5 HP and watching the storm finish them costs
    // you the kill AND the bounty, which taxes exactly the aggression the whole
    // health economy is built to reward.
    if (attackerId === ZONE_SOURCE_ID) {
        aux.lastZoneTick = world.tick;
    }
    else {
        aux.lastHitById = attackerId;
        aux.lastHitAbilityId = abilityId;
        aux.lastHitTick = world.tick;
    }
    // Ult charges from damage dealt and damage taken, and from nothing else.
    // Time alive was considered and rejected: it pays players to hide, which in a
    // winner-take-all royale is already the dominant strategy without help.
    target.ultCharge = Math.min(ULT_FULL, target.ultCharge + dmg * ULT_CHARGE_PER_DAMAGE_TAKEN);
    if (attackerId !== target.id && attackerId !== ZONE_SOURCE_ID) {
        const attacker = findPlayer(world, attackerId);
        if (attacker) {
            attacker.damageDealt += dmg;
            attacker.ultCharge = Math.min(ULT_FULL, attacker.ultCharge + dmg * ULT_CHARGE_PER_DAMAGE_DEALT);
        }
    }
    if (report) {
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId,
            targetId: target.id,
            abilityId,
            damage: dmg,
            x: target.x,
            y: target.y,
            z: target.z,
        });
    }
    return dmg;
}
/** Awards the kill bounty. The only way HP comes back in this game. */
export function awardKill(world, killer, abilityId) {
    killer.kills += 1;
    killer.health = Math.min(maxHealth(killer), killer.health + KILL_HEAL);
    killer.ultCharge = Math.min(ULT_FULL, killer.ultCharge + KILL_ULT_CHARGE);
    refundCooldownOnKill(world, killer, abilityId);
}
/** Volt's Blink Strike returns half its cooldown when it finishes someone. */
function refundCooldownOnKill(world, killer, abilityId) {
    const character = getCharacter(killer.characterId);
    for (const slot of SLOTS) {
        const ability = character.abilities[slot];
        if (ability.id !== abilityId)
            continue;
        for (const e of ability.effects ?? []) {
            if (e.kind !== "refund_cooldown_on_kill")
                continue;
            const readyTick = killer.cooldowns[abilityId] ?? 0;
            const remaining = readyTick - world.tick;
            if (remaining <= 0)
                return;
            killer.cooldowns[abilityId] = world.tick + Math.floor((remaining * (100 - e.pct)) / 100);
        }
        return;
    }
}
const SLOTS = ["basic", "skill1", "skill2", "ultimate"];
// ---------------------------------------------------------------------------
// Casting
// ---------------------------------------------------------------------------
export function abilityReady(world, p, ability, slot) {
    if (world.tick < (p.cooldowns[ability.id] ?? 0))
        return false;
    if (slot === "ultimate") {
        if (p.ultCharge < ULT_FULL)
            return false;
        if (hasEffect(p, "suppress_abilities"))
            return false;
    }
    return true;
}
/** Commits a player to a cast. Returns the event, or null if it could not start. */
export function startCast(world, p, slot, events) {
    const ability = abilityForSlot(p.characterId, slot);
    if (!abilityReady(world, p, ability, slot))
        return false;
    if (slot === "ultimate")
        p.ultCharge = 0;
    if (ability.cooldownMs > 0)
        p.cooldowns[ability.id] = world.tick + msToTicks(ability.cooldownMs);
    const committedTicks = msToTicks(ability.windupMs + ability.recoveryMs);
    if (committedTicks > 0) {
        applyEffect(p, "busy", ability.windupMs + ability.recoveryMs, world.tick, p.id);
    }
    world.casts.push({
        playerId: p.id,
        abilityId: ability.id,
        slot,
        resolveTick: world.tick + msToTicks(ability.windupMs),
        yaw: p.yaw,
        x: p.x,
        y: p.y,
        z: p.z,
    });
    events.push({
        t: "ability_cast",
        tick: world.tick,
        playerId: p.id,
        abilityId: ability.id,
        x: p.x,
        z: p.z,
        yaw: p.yaw,
    });
    return true;
}
/** True while this player has a committed cast whose wind-up has not resolved. */
export function hasPendingCast(world, playerId) {
    for (const c of world.casts)
        if (c.playerId === playerId)
            return true;
    return false;
}
/** The universal dash: one charge, 6s, and the only i-frames in the game. */
export function tryDash(world, p, moveX, moveZ, events) {
    if (hasEffect(p, "no_dash"))
        return false;
    if (hasEffect(p, "dash"))
        return false;
    if (world.tick < (p.cooldowns[DASH_ABILITY_ID] ?? 0))
        return false;
    const fwd = forwardFromYaw(p.yaw);
    let dx = fwd.x;
    let dz = fwd.z;
    const mag = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (mag > 0.01) {
        // Dash where the stick points if the stick is pointing anywhere; otherwise
        // dash where you are looking. The right vector MUST be `rightFromYaw` — a
        // hand-rolled `(fwd.z, -fwd.x)` here is its exact negation, which mirrored
        // every strafing dash across the forward axis: hold D, dash left.
        const right = rightFromYaw(p.yaw);
        const rx = fwd.x * moveZ + right.x * moveX;
        const rz = fwd.z * moveZ + right.z * moveX;
        const rlen = Math.sqrt(rx * rx + rz * rz);
        if (rlen > 1e-6) {
            dx = rx / rlen;
            dz = rz / rlen;
        }
    }
    p.cooldowns[DASH_ABILITY_ID] = world.tick + msToTicks(DASH_COOLDOWN_MS);
    applyEffect(p, "dash", DASH_DURATION_MS, world.tick, p.id, { ax: dx, az: dz });
    applyEffect(p, "iframe", DASH_DURATION_MS, world.tick, p.id);
    // A dash reads as a horizontal move. Reduced in-dash gravity already sells
    // that from a standstill, but it cannot un-earn velocity: dashing at terminal
    // velocity stayed a full-speed dive. Shed the downward component instead.
    if (p.vy < 0)
        p.vy = 0;
    events.push({
        t: "ability_cast",
        tick: world.tick,
        playerId: p.id,
        abilityId: DASH_ABILITY_ID,
        x: p.x,
        z: p.z,
        yaw: p.yaw,
    });
    return true;
}
// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------
/**
 * Resolves every cast whose wind-up has elapsed.
 *
 * Casts are appended in sorted-player order every tick and drained in array
 * order, so resolution order is stable across runtimes.
 */
export function resolvePendingCasts(world, events) {
    if (world.casts.length === 0)
        return;
    const pending = world.casts;
    const keep = [];
    for (const cast of pending) {
        if (world.tick < cast.resolveTick) {
            keep.push(cast);
            continue;
        }
        const caster = findPlayer(world, cast.playerId);
        // Dying during your own wind-up cancels the cast. Fault Line landing from
        // beyond the grave would make trading with Monolith strictly bad.
        if (!caster || caster.phase !== "alive")
            continue;
        const ability = findAbilityById(caster.characterId, cast.abilityId);
        if (!ability)
            continue;
        resolveAbility(world, caster, ability, cast, events);
    }
    world.casts = keep;
}
function findAbilityById(characterId, abilityId) {
    const c = getCharacter(characterId);
    for (const slot of SLOTS) {
        const a = c.abilities[slot];
        if (a.id === abilityId)
            return a;
    }
    return null;
}
function resolveAbility(world, caster, ability, cast, events) {
    const effects = ability.effects ?? [];
    const damage = ability.damage * getCharacter(caster.characterId).damageMult;
    const yaw = cast.yaw;
    // WORLD-anchored shapes resolve from the FROZEN cast origin: their telegraph
    // is a place drawn on the floor at commit, so reading caster.x/z would let
    // anyone who kept moving through their own wind-up drag the real shape off
    // the drawn one — stepping off the decal is dodging, and it has to work.
    // BODY-anchored shapes resolve from the LIVE body: freezing them (the first,
    // too-broad form of that fix) left a 90ms melee cone hanging in the air
    // behind a walking caster while the client drew it on the moving character.
    // The split is @chaos/protocol's abilityAnchor, shared with the client's
    // telegraphs and VFX, so hit, telegraph and effect stay the same place.
    const frozen = abilityAnchor(ability) === "world";
    const ox = frozen ? cast.x : caster.x;
    const oy = frozen ? cast.y : caster.y;
    const oz = frozen ? cast.z : caster.z;
    const fwd = forwardFromYaw(yaw);
    switch (ability.hit.kind) {
        case "self": {
            // The one shape with no origin: it targets the caster's live body, so
            // there is nothing to freeze — Overload buffs Volt wherever he is, and
            // Perch lifts Talon from where he actually stands, not from where he
            // pressed the button.
            applyAbilityEffects(world, caster.id, caster, effects, events);
            const lift = ability.selfMove ?? 0;
            if (lift > 0) {
                // Talon's Perch is the only self-move on a "self" shape: straight up.
                caster.y += lift;
                caster.vy = 0;
            }
            break;
        }
        case "melee_cone": {
            const range = ability.hit.range + PLAYER_RADIUS;
            const cosHalf = cosDeg(ability.hit.halfAngleDeg);
            const sinHalf = sinDeg(ability.hit.halfAngleDeg);
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                const dx = target.x - ox;
                const dz = target.z - oz;
                const d2 = dx * dx + dz * dz;
                if (d2 > range * range)
                    continue;
                // A swing has a height. Without this a grounded basic reached a Perched
                // Talon 8m straight up, at full damage.
                if (Math.abs(target.y - oy) > MELEE_VERTICAL_REACH)
                    continue;
                // Inside the wedge, or body grazing its edge: `cross*cos - dot*sin` is
                // the exact distance from the target's centre to the cone's boundary
                // ray, so a body half-inside the 55° edge counts — the same
                // body-radius grammar the range check above already grants.
                const dot = dx * fwd.x + dz * fwd.z;
                const cross = Math.abs(dx * fwd.z - dz * fwd.x);
                const inside = dot >= Math.sqrt(d2) * cosHalf;
                const grazes = cross * cosHalf - dot * sinHalf <= PLAYER_RADIUS;
                if (!inside && !grazes)
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, ox, oz);
            }
            destroyDecoysInSphere(world, ox, oy, oz, range, events);
            break;
        }
        case "sphere": {
            // One hit grammar everywhere: cones and lines already pad by body radius,
            // so "my body touches the drawn circle" must be a hit here too. Without
            // the pad, circles under-delivered by 0.5m while cones over-delivered —
            // two rules the player was never told apart.
            const radius = ability.hit.radius + PLAYER_RADIUS;
            const chain = findEffectDef(effects, "chain");
            const swap = findEffectDef(effects, "swap_position");
            const pull = findEffectDef(effects, "pull");
            if (swap) {
                resolveSwap(world, caster, ox, oy, oz, radius, events);
                break;
            }
            if (chain) {
                resolveChain(world, caster, ability, damage, ox, oy, oz, radius, chain, effects, events);
                break;
            }
            if (pull) {
                // Gravity Well is body-anchored: it drags everyone toward where Hex
                // actually stands at resolve, the point the tracking decal has
                // followed her to.
                for (const target of world.players) {
                    if (!isEnemy(caster, target))
                        continue;
                    if (dist2XZ(target.x, target.z, ox, oz) > radius * radius)
                        continue;
                    applyEffect(target, "pull", PULL_DURATION_MS, world.tick, caster.id, {
                        magnitude: pull.kind === "pull" ? pull.strength : 0,
                        ax: ox,
                        az: oz,
                    });
                    events.push({
                        t: "effect_applied",
                        tick: world.tick,
                        playerId: target.id,
                        effect: "pull",
                        durationMs: PULL_DURATION_MS,
                    });
                }
                break;
            }
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                if (dist2(target.x, target.y, target.z, ox, oy, oz) > radius * radius)
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, ox, oz);
            }
            destroyDecoysInSphere(world, ox, oy, oz, radius, events);
            break;
        }
        case "dash_line": {
            const length = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : ability.hit.length;
            const startX = ox;
            const startZ = oz;
            let endX = ox + fwd.x * length;
            let endZ = oz + fwd.z * length;
            // Blink Strike moves the caster; Fault Line only cracks the ground. The
            // anchor split above already picked the right start: Fault Line's line
            // is world-anchored (ox/oz frozen, the crack stays on the drawn floor),
            // while Blink Strike is your own body dashing, so its line runs from
            // wherever you actually are at resolve — sweeping a displaced caster
            // from the stale cast position would teleport them back to it first.
            if ((ability.selfMove ?? 0) > 0) {
                const swept = sweepPoint(ox, oy, oz, fwd.x, fwd.z, length, world.platforms);
                caster.x = swept.x;
                caster.z = swept.z;
                endX = swept.x;
                endZ = swept.z;
            }
            // Rectangle plus body pad, NOT a capsule around the spine: the capsule
            // form put a radial cap of the full half-width on the segment end, so
            // the drawn tip under-promised by width/2 — Fault Line hit 1.5m past
            // the end of its own decal. Now every edge over-delivers by exactly the
            // body radius, the one grammar all the other shapes speak.
            const halfWidth = ability.hit.width * 0.5;
            const pad2 = PLAYER_RADIUS * PLAYER_RADIUS;
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                if (pointRectDist2XZ(target.x, target.z, startX, startZ, endX, endZ, halfWidth) > pad2)
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, startX, startZ);
            }
            destroyDecoysOnSegment(world, startX, startZ, endX, endZ, halfWidth, events);
            break;
        }
        case "projectile": {
            spawnProjectile(world, caster, ability, damage, yaw, ox, oy, oz);
            break;
        }
        case "ground_zone": {
            spawnGroundZone(world, caster, ability, damage, effects, yaw, ox, oy, oz);
            break;
        }
    }
    // Effects that live on the caster regardless of what the shape hit.
    const decoy = findEffectDef(effects, "decoy");
    if (decoy && decoy.kind === "decoy")
        spawnDecoy(world, caster, decoy.durationMs);
}
function isEnemy(caster, target) {
    return target.id !== caster.id && target.phase === "alive";
}
function hitTarget(world, caster, target, ability, damage, effects, events, 
// Where the hit came from: the shape's own resolve origin (frozen for
// world-anchored abilities, the live body for body-anchored ones, the
// previous link for a chain), so knockback radiates out of the shape that
// actually hit rather than away from somewhere else entirely.
fromX, fromZ) {
    const applied = applyDamage(world, target, damage, caster.id, ability.id, events);
    // Immunity blocks the rider effects too — i-frames through a Fault Line means
    // through the stun as well, or the dash never actually saved you.
    if (damage > 0 && applied <= 0)
        return 0;
    applyKnockback(target, fromX, fromZ, ability.knockback ?? 0);
    applyAbilityEffects(world, caster.id, target, effects, events);
    return applied;
}
function applyKnockback(target, fromX, fromZ, metres) {
    if (metres <= 0)
        return;
    const dx = target.x - fromX;
    const dz = target.z - fromZ;
    const d = Math.sqrt(dx * dx + dz * dz);
    const speed = metres * KNOCKBACK_SPEED_PER_UNIT;
    if (d < 1e-6) {
        target.vy += speed * KNOCKBACK_LIFT_FRACTION;
        return;
    }
    target.vx += (dx / d) * speed;
    target.vz += (dz / d) * speed;
    target.vy += speed * KNOCKBACK_LIFT_FRACTION;
}
function applyAbilityEffects(world, casterId, target, effects, events) {
    for (const e of effects) {
        switch (e.kind) {
            case "slow":
            case "speed":
            case "damage_reduction":
                applyEffect(target, e.kind, e.durationMs, world.tick, casterId, { magnitude: e.pct });
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                // Volt's Overload states slow immunity in prose; this is that, as state.
                if (e.kind === "speed" && target.id === casterId) {
                    applyEffect(target, "slow_immune", e.durationMs, world.tick, casterId);
                }
                break;
            case "stun":
            case "invisible":
            case "no_dash":
            case "hover":
            case "suppress_abilities":
                applyEffect(target, e.kind, e.durationMs, world.tick, casterId);
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                break;
            case "damage_over_time":
                applyEffect(target, "damage_over_time", e.durationMs, world.tick, casterId, { magnitude: e.dps });
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                break;
            // Handled by the shape resolver, not here.
            case "pull":
            case "swap_position":
            case "chain":
            case "repeat":
            case "decoy":
            case "refund_cooldown_on_kill":
                break;
        }
    }
}
function reportEffect(world, events, playerId, effect, durationMs) {
    events.push({ t: "effect_applied", tick: world.tick, playerId, effect, durationMs });
}
function findEffectDef(effects, kind) {
    for (const e of effects)
        if (e.kind === kind)
            return e;
    return null;
}
/**
 * Mirage's Displace: swap with the nearest valid target inside the radius.
 *
 * Body-anchored, so acquisition measures from Mirage's live position — the
 * circle travels with her — and the swap trades the two live bodies.
 */
function resolveSwap(world, caster, ox, oy, oz, radius, events) {
    const target = nearestTarget(world, caster, ox, oy, oz, radius);
    if (!target)
        return;
    if (target.kind === "decoy") {
        // Swapping with a decoy is a legal, funny outcome. It still moves you.
        const d = target.decoy;
        const tx = caster.x;
        const ty = caster.y;
        const tz = caster.z;
        caster.x = d.x;
        caster.y = d.y;
        caster.z = d.z;
        d.x = tx;
        d.y = ty;
        d.z = tz;
        return;
    }
    const other = target.player;
    const tx = caster.x;
    const ty = caster.y;
    const tz = caster.z;
    caster.x = other.x;
    caster.y = other.y;
    caster.z = other.z;
    other.x = tx;
    other.y = ty;
    other.z = tz;
    events.push({
        t: "effect_applied",
        tick: world.tick,
        playerId: other.id,
        effect: "swap_position",
        durationMs: 0,
    });
}
/** Volt's Chain Lightning: nearest target, then two arcs at 60% each. */
function resolveChain(world, caster, ability, damage, ox, oy, oz, radius, chain, effects, events) {
    if (chain.kind !== "chain")
        return;
    // First strike acquires from Volt's live body — the lightning arcs out of
    // him, not out of a spot he left — and hops then arc from wherever each
    // struck body actually is: the chain is drawn link to link.
    const first = nearestTarget(world, caster, ox, oy, oz, radius);
    if (!first)
        return;
    if (first.kind === "decoy") {
        // The decoy eats the ultimate. That is the entire point of the decoy.
        destroyDecoy(world, first.decoy.id, caster.id, ability.id, damage, events);
        return;
    }
    const struck = new Set();
    let current = first.player;
    let dmg = damage;
    hitTarget(world, caster, current, ability, dmg, effects, events, ox, oz);
    struck.add(current.id);
    for (let hop = 0; hop < chain.maxTargets - 1; hop++) {
        let best = null;
        // Padded by body radius like acquisition above and every other distance in
        // the game — this was the one unpadded check, so a body grazing the hop
        // range escaped an arc that the same body would have caught anywhere else.
        const hopRange = chain.radius + PLAYER_RADIUS;
        let bestD2 = hopRange * hopRange;
        for (const candidate of world.players) {
            if (!isEnemy(caster, candidate) || struck.has(candidate.id))
                continue;
            const d2 = dist2(candidate.x, candidate.y, candidate.z, current.x, current.y, current.z);
            if (d2 <= bestD2) {
                bestD2 = d2;
                best = candidate;
            }
        }
        if (!best)
            break;
        dmg = (dmg * chain.falloffPct) / 100;
        hitTarget(world, caster, best, ability, dmg, effects, events, current.x, current.z);
        struck.add(best.id);
        current = best;
    }
}
/**
 * Nearest enemy or decoy, ties broken by id so both runtimes agree.
 *
 * Decoys are candidates here on purpose: a decoy that could not be picked as
 * "nearest target" would be identified instantly by every single-target
 * ability in the game.
 */
function nearestTarget(world, caster, ox, oy, oz, radius) {
    let best = null;
    let bestD2 = radius * radius;
    let bestId = "";
    for (const p of world.players) {
        if (!isEnemy(caster, p))
            continue;
        const d2 = dist2(p.x, p.y, p.z, ox, oy, oz);
        if (d2 < bestD2 || (d2 === bestD2 && best !== null && p.id < bestId)) {
            bestD2 = d2;
            best = { kind: "player", player: p };
            bestId = p.id;
        }
    }
    for (const d of world.decoys) {
        if (d.ownerId === caster.id)
            continue;
        const d2 = dist2(d.x, d.y, d.z, ox, oy, oz);
        if (d2 < bestD2 || (d2 === bestD2 && best !== null && d.id < bestId)) {
            bestD2 = d2;
            best = { kind: "decoy", decoy: d };
            bestId = d.id;
        }
    }
    return best;
}
// ---------------------------------------------------------------------------
// Projectiles
// ---------------------------------------------------------------------------
function spawnProjectile(world, caster, ability, damage, yaw, ox, oy, oz) {
    if (ability.hit.kind !== "projectile")
        return;
    const fwd = forwardFromYaw(yaw);
    const speed = ability.hit.speed;
    world.nextEntityId += 1;
    world.projectiles.push({
        id: `proj#${world.nextEntityId}`,
        ownerId: caster.id,
        abilityId: ability.id,
        // Launched from the frozen cast origin: the wind-up flash is drawn there,
        // and a barrel that follows the caster re-aims the shot the yaw freeze
        // exists to pin down.
        x: ox + fwd.x * PROJECTILE_SPAWN_OFFSET,
        y: oy + 0.4,
        z: oz + fwd.z * PROJECTILE_SPAWN_OFFSET,
        vx: fwd.x * speed * PROJECTILE_LAUNCH_COS,
        vy: speed * PROJECTILE_LAUNCH_SIN,
        vz: fwd.z * speed * PROJECTILE_LAUNCH_COS,
        gravity: -ability.hit.gravity,
        radius: ability.hit.radius,
        damage,
        remainingRange: ability.hit.maxRange,
    });
}
export function stepProjectiles(world, events) {
    if (world.projectiles.length === 0)
        return;
    const keep = [];
    for (const proj of world.projectiles) {
        proj.vy += proj.gravity * DT;
        const dx = proj.vx * DT;
        const dy = proj.vy * DT;
        const dz = proj.vz * DT;
        proj.x += dx;
        proj.y += dy;
        proj.z += dz;
        proj.remainingRange -= Math.sqrt(dx * dx + dz * dz);
        if (proj.remainingRange <= 0 || proj.y < world.map.killY || hitsGeometry(world, proj) || hitsSomeone(world, proj)) {
            detonate(world, proj, events);
            continue;
        }
        keep.push(proj);
    }
    world.projectiles = keep;
}
const projectileCandidates = [];
function hitsGeometry(world, proj) {
    if (world.map.floor.kind !== "none" && proj.y <= world.map.floor.y)
        return true;
    const candidates = world.hash.queryInto(proj.x, proj.z, projectileCandidates);
    for (const index of candidates) {
        const plat = world.platforms[index];
        if (!plat || !plat.solid)
            continue;
        if (Math.abs(proj.x - plat.x) <= plat.hx &&
            Math.abs(proj.y - plat.y) <= plat.hy &&
            Math.abs(proj.z - plat.z) <= plat.hz) {
            return true;
        }
    }
    return false;
}
function hitsSomeone(world, proj) {
    const reach = PLAYER_RADIUS + PROJECTILE_CONTACT_RADIUS;
    for (const p of world.players) {
        if (p.id === proj.ownerId || p.phase !== "alive")
            continue;
        // Point-vs-capsule, with the real rounded cap. The AABB helper treats the
        // capsule as flat-topped, which shaves the cap radius off the vertical
        // reach — precisely the margin the flat Arc Shot arc flies at, so a shell
        // that visibly skimmed a standing target's head read as a pass-through.
        const cy = clamp(proj.y, p.y - PLAYER_HALF_HEIGHT, p.y + PLAYER_HALF_HEIGHT);
        if (dist2(proj.x, proj.y, proj.z, p.x, cy, p.z) <= reach * reach)
            return true;
    }
    return false;
}
/**
 * How far above or below a burst still counts as caught in it.
 *
 * Splash is measured on the GROUND PLANE with a vertical band, not as a 3D
 * sphere. A shell that bursts in the air — because it ran out of range, clipped
 * a wall, or detonated over someone's head — is still an explosion, and a 3D
 * test makes it silently harmless: at radius 2.5 a burst 3m up cannot reach a
 * player standing directly underneath. That is precisely how Arc Shot came to be
 * unable to hit ANY grounded target at ANY distance on flat ground.
 */
const SPLASH_VERTICAL_REACH = 4;
function detonate(world, proj, events) {
    // Padded by body radius: same grammar as every other circle.
    const r = proj.radius + PLAYER_RADIUS;
    const r2 = r * r;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        if (p.id === proj.ownerId)
            continue;
        const dx = p.x - proj.x;
        const dz = p.z - proj.z;
        if (dx * dx + dz * dz > r2)
            continue;
        if (Math.abs(p.y - proj.y) > SPLASH_VERTICAL_REACH)
            continue;
        applyDamage(world, p, proj.damage, proj.ownerId, proj.abilityId, events);
    }
    destroyDecoysInSphere(world, proj.x, proj.y, proj.z, r, events);
}
// ---------------------------------------------------------------------------
// Ground zones
// ---------------------------------------------------------------------------
function spawnGroundZone(world, caster, ability, damage, effects, yaw, ox, oy, oz) {
    if (ability.hit.kind !== "ground_zone")
        return;
    const repeat = findEffectDef(effects, "repeat");
    const offset = GROUND_ZONE_OFFSET[ability.id] ?? 0;
    const fwd = forwardFromYaw(yaw);
    const kind = repeat ? "barrage" : ability.damage > 0 ? "trap" : "aura";
    world.nextEntityId += 1;
    const zone = {
        id: `zone#${world.nextEntityId}`,
        ownerId: caster.id,
        abilityId: ability.id,
        kind,
        // Placed off the frozen cast origin, so the circle the shells actually
        // fall in is the circle the decal drew at commit.
        x: ox + fwd.x * offset,
        y: oy,
        z: oz + fwd.z * offset,
        radius: ability.hit.radius,
        damage,
        effects: effects.slice(),
        expiresTick: world.tick + msToTicks(ability.hit.durationMs),
        armedTick: world.tick + (kind === "trap" ? TRAP_ARM_TICKS : 0),
        pulsesLeft: repeat && repeat.kind === "repeat" ? repeat.count : 0,
        pulseIntervalTicks: repeat && repeat.kind === "repeat" ? msToTicks(repeat.intervalMs) : 0,
        nextPulseTick: world.tick,
    };
    world.zones.push(zone);
}
export function stepGroundZones(world, events) {
    if (world.zones.length === 0)
        return;
    const keep = [];
    for (const zone of world.zones) {
        let alive = world.tick < zone.expiresTick;
        if (alive) {
            switch (zone.kind) {
                case "trap":
                    alive = stepTrap(world, zone, events);
                    break;
                case "aura":
                    stepAura(world, zone, events);
                    break;
                case "barrage":
                    alive = stepBarrage(world, zone, events);
                    break;
            }
        }
        if (alive)
            keep.push(zone);
    }
    world.zones = keep;
}
/** Returns false once the trap has been consumed. */
function stepTrap(world, zone, events) {
    if (world.tick < zone.armedTick)
        return true;
    const r = zone.radius + PLAYER_RADIUS;
    for (const p of world.players) {
        if (p.phase !== "alive" || p.id === zone.ownerId)
            continue;
        if (dist2XZ(p.x, p.z, zone.x, zone.z) > r * r)
            continue;
        if (Math.abs(p.y - zone.y) > PLAYER_HALF_HEIGHT * 2)
            continue;
        const applied = applyDamage(world, p, zone.damage, zone.ownerId, zone.abilityId, events);
        // A trap that landed on an i-framed dasher still burns itself out — that is
        // what makes dashing through a glyph a real play rather than a free pass.
        if (applied > 0 || zone.damage <= 0) {
            applyAbilityEffects(world, zone.ownerId, p, zone.effects, events);
        }
        return false;
    }
    return true;
}
/**
 * Hex's Null Zone. The caster is exempt: a dome that suppressed and burned its
 * own owner would be strictly self-harming, and Hex already carries the lowest
 * damage in the roster. The asymmetry is stated rather than hidden.
 */
function stepAura(world, zone, events) {
    const shouldReport = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    const r = zone.radius + PLAYER_RADIUS;
    // Direct hits scale `ability.damage` by the caster's damageMult at resolve,
    // and trap/barrage inherit that through the pre-multiplied `zone.damage`.
    // The aura burn reads `e.dps` straight off the authored effect — left raw it
    // was the one damage source that skipped Hex's 0.9, ticking 8 HP/s where her
    // multiplier says 7.2.
    const owner = findPlayer(world, zone.ownerId);
    const dmgMult = owner ? getCharacter(owner.characterId).damageMult : 1;
    for (const p of world.players) {
        if (p.phase !== "alive" || p.id === zone.ownerId)
            continue;
        // The client draws Null Zone as a hemisphere of the ability's radius, so
        // the suppression volume is that same sphere. A flat XZ test made the dome
        // an infinite column: a Talon perched 25m over the floor circle was
        // silenced, dash-blocked and burned while visibly nowhere near it.
        if (dist2(p.x, p.y, p.z, zone.x, zone.y, zone.z) > r * r)
            continue;
        // Refreshed with a two-tick lifetime so walking out of the dome ends the
        // suppression immediately rather than a full duration later.
        for (const e of zone.effects) {
            if (e.kind === "suppress_abilities" || e.kind === "no_dash") {
                applyEffect(p, e.kind, ticksToMs(2), world.tick, zone.ownerId);
            }
            else if (e.kind === "damage_over_time") {
                applyDamage(world, p, e.dps * dmgMult * DT, zone.ownerId, zone.abilityId, events, shouldReport);
            }
        }
    }
}
/** Talon's Barrage: shells at seeded offsets inside the target area. */
function stepBarrage(world, zone, events) {
    const splash = BARRAGE_SHELL_SPLASH + PLAYER_RADIUS;
    // Shell CENTRES stay a full splash radius inside the ring, so no shell's
    // blast escapes the drawn circle. Centres used to land anywhere in the ring
    // and splash 4m past it — a 16m kill zone under a 12m telegraph, killing
    // players who had dodged correctly. Standing on the drawn edge is still a
    // hit (rim centre + splash + body pad), so the whole ring stays dangerous.
    const spread = Math.max(0, zone.radius - BARRAGE_SHELL_SPLASH);
    while (zone.pulsesLeft > 0 && world.tick >= zone.nextPulseTick) {
        const offset = nextPointInDisc(world, spread);
        const sx = zone.x + offset.dx;
        const sz = zone.z + offset.dz;
        for (const p of world.players) {
            if (p.phase !== "alive" || p.id === zone.ownerId)
                continue;
            if (dist2XZ(p.x, p.z, sx, sz) > splash * splash)
                continue;
            // Same vertical band as every other splash. Without it a shell bursting
            // on the drawn floor hit a Talon perched 8m over the circle — or anyone
            // on a platform above or below it — at full damage.
            if (Math.abs(p.y - zone.y) > SPLASH_VERTICAL_REACH)
                continue;
            applyDamage(world, p, zone.damage, zone.ownerId, zone.abilityId, events);
        }
        destroyDecoysInSphere(world, sx, zone.y, sz, splash, events);
        zone.pulsesLeft -= 1;
        zone.nextPulseTick += Math.max(1, zone.pulseIntervalTicks);
    }
    return zone.pulsesLeft > 0;
}
// ---------------------------------------------------------------------------
// Damage over time
// ---------------------------------------------------------------------------
export function applyDamageOverTime(world, events) {
    const shouldReport = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        for (const e of p.effects) {
            if (e.kind !== "damage_over_time")
                continue;
            applyDamage(world, p, e.magnitude * DT, e.sourceId, "dot", events, shouldReport);
        }
    }
}
// ---------------------------------------------------------------------------
// Decoys
// ---------------------------------------------------------------------------
export function recordPath(aux, p) {
    const i = aux.pathHead;
    aux.pathX[i] = p.x;
    aux.pathY[i] = p.y;
    aux.pathZ[i] = p.z;
    aux.pathYaw[i] = p.yaw;
    aux.pathHead = (i + 1) % PATH_HISTORY_TICKS;
}
function spawnDecoy(world, caster, durationMs) {
    const aux = getAux(world, caster.id);
    const px = [];
    const py = [];
    const pz = [];
    const pyaw = [];
    // Unroll the ring buffer oldest-first, so the decoy replays the last two
    // seconds in the order they happened.
    for (let k = 0; k < PATH_HISTORY_TICKS; k++) {
        const i = (aux.pathHead + k) % PATH_HISTORY_TICKS;
        px.push(aux.pathX[i] ?? caster.x);
        py.push(aux.pathY[i] ?? caster.y);
        pz.push(aux.pathZ[i] ?? caster.z);
        pyaw.push(aux.pathYaw[i] ?? caster.yaw);
    }
    world.nextEntityId += 1;
    world.decoys.push({
        id: `${DECOY_ID_PREFIX}${world.nextEntityId}`,
        ownerId: caster.id,
        characterId: caster.characterId,
        x: caster.x,
        y: caster.y,
        z: caster.z,
        yaw: caster.yaw,
        expiresTick: world.tick + msToTicks(durationMs),
        pathX: px,
        pathY: py,
        pathZ: pz,
        pathYaw: pyaw,
        cursor: 0,
    });
}
export function stepDecoys(world) {
    if (world.decoys.length === 0)
        return;
    const keep = [];
    for (const d of world.decoys) {
        if (world.tick >= d.expiresTick)
            continue;
        const i = d.cursor % d.pathX.length;
        d.x = d.pathX[i] ?? d.x;
        d.y = d.pathY[i] ?? d.y;
        d.z = d.pathZ[i] ?? d.z;
        d.yaw = d.pathYaw[i] ?? d.yaw;
        d.cursor += 1;
        keep.push(d);
    }
    world.decoys = keep;
}
function destroyDecoy(world, decoyId, attackerId, abilityId, damage, events) {
    const i = world.decoys.findIndex((d) => d.id === decoyId);
    if (i < 0)
        return;
    const d = world.decoys[i];
    if (!d)
        return;
    // Reported with its nominal damage so the attacker's client shows a normal
    // hit. Nothing is credited: a decoy is not damage dealt and not ult charge.
    events.push({
        t: "hit",
        tick: world.tick,
        attackerId,
        targetId: d.id,
        abilityId,
        damage,
        x: d.x,
        y: d.y,
        z: d.z,
    });
    world.decoys.splice(i, 1);
}
function destroyDecoysInSphere(world, x, y, z, radius, events) {
    for (let i = world.decoys.length - 1; i >= 0; i--) {
        const d = world.decoys[i];
        if (!d)
            continue;
        if (dist2(d.x, d.y, d.z, x, y, z) > radius * radius)
            continue;
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId: "",
            targetId: d.id,
            abilityId: "",
            damage: 0,
            x: d.x,
            y: d.y,
            z: d.z,
        });
        world.decoys.splice(i, 1);
    }
}
function destroyDecoysOnSegment(world, ax, az, bx, bz, halfWidth, events) {
    for (let i = world.decoys.length - 1; i >= 0; i--) {
        const d = world.decoys[i];
        if (!d)
            continue;
        // Same rounded rectangle the live-target test uses: a decoy that survives
        // a hit its owner would have taken gives the disguise away.
        if (pointRectDist2XZ(d.x, d.z, ax, az, bx, bz, halfWidth) > PLAYER_RADIUS * PLAYER_RADIUS)
            continue;
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId: "",
            targetId: d.id,
            abilityId: "",
            damage: 0,
            x: d.x,
            y: d.y,
            z: d.z,
        });
        world.decoys.splice(i, 1);
    }
}
export function creditFor(world, victim) {
    const aux = getAux(world, victim.id);
    const recentPlayerHit = aux.lastHitById !== null &&
        aux.lastHitById !== victim.id &&
        world.tick - aux.lastHitTick <= KILL_CREDIT_TICKS;
    if (recentPlayerHit && aux.lastHitById !== null) {
        return { killerId: aux.lastHitById, abilityId: aux.lastHitAbilityId, byZone: false };
    }
    if (world.tick - aux.lastZoneTick <= KILL_CREDIT_TICKS) {
        return { killerId: null, abilityId: "zone", byZone: true };
    }
    return { killerId: null, abilityId: "", byZone: false };
}
/** Cosine of an angle given in degrees, using the deterministic series. */
function cosDeg(deg) {
    return dcos((deg * PI) / 180);
}
/** Sine of an angle given in degrees, using the deterministic series. */
function sinDeg(deg) {
    return dsin((deg * PI) / 180);
}
//# sourceMappingURL=combat.js.map